import api from "../api";
import {
  trackBookingAccepted,
  trackBookingRejected,
  trackDriverRegistered,
} from "../analytics-events";
import { appendFileToFormData } from "../form-data";
import {Driver as UserDriver} from "../types/user";
import type { Booking } from "./bookings";
import type { Quote } from "./quotes";
import { DriverFormData } from "../validation/driver";

export interface Driver {
  driver_id: number;
  name: string;
  email: string;
  phone: string;
  reputation_score: number;
  total_ratings_count: number;
  is_top_rated: boolean;
  distance_from_pickup_km: number;
  estimated_arrival_minutes: number;
  base_price: number | null;
  current_location: {
    latitude: number;
    longitude: number;
  };
}

export interface AvailableDriversParams {
  from_latitude: number;
  from_longitude: number;
  to_latitude?: number;
  to_longitude?: number;
  service_type: string;
  radius_km?: number;
}

export interface ScheduledJob {
  booking_id: number;
  vehicle_id: number;
  distance_km: number;
  scheduled_at: string;
  my_quote: Quote | null;
  booking: Booking;
}

class DriverService {
  async getScheduledJobs(): Promise<ScheduledJob[]> {
    const response = await api.get("/drivers/scheduled-jobs");
    return response.data.data;
  }

  async getAvailableDrivers(params: AvailableDriversParams): Promise<{
    data: Driver[];
    meta: {
      total_available_drivers: number;
      trip_distance_miles: number | null;
      trip_duration_minutes: number | null;
    };
  }> {
    const response = await api.get("/bookings/available-drivers", { params });
    return response.data;
  }

  async acceptBooking(
    bookingId: number,
    vehicleId: number,
    amount?: number
  ): Promise<any> {
    const response = await api.post(`/bookings/${bookingId}/accept`, {
      vehicle_id: vehicleId,
      amount,
    });
    trackBookingAccepted(bookingId);
    return response.data;
  }

  async rejectBooking(bookingId: number, reason?: string): Promise<any> {
    const response = await api.post(`/bookings/${bookingId}/reject`, {
      reason,
    });
    trackBookingRejected(bookingId);
    return response.data;
  }

  async createDriver(data: DriverFormData): Promise<UserDriver> {
    const formData = new FormData();

    // Add basic fields
    Object.keys(data).forEach(key => {
      if (data[key as keyof DriverFormData] !== undefined && !['document'].includes(key)) {
        formData.append(key, String(data[key as keyof DriverFormData]));
      }
    });

    if (data.document) {
      await appendFileToFormData(formData, "document", data.document);
    }

    const response = await api.post("/drivers", formData);
    trackDriverRegistered();
    return response.data.data;
  }

   async updateDriver(
    id: number,
    data: Partial<DriverFormData>
  ): Promise<UserDriver> {
    const response = await api.put(`/drivers/${id}`, data);
    return response.data.data;
  }

  async uploadDocument(driverId: number, type: string, file: any): Promise<Document> {
    const formData = new FormData();
    formData.append('driver_id', String(driverId));
    formData.append('type', type);
    await appendFileToFormData(formData, "file", {
      uri: file.uri,
      type: file.mimeType || file.type || "image/jpeg",
      name: file.fileName || file.name || `${type}.jpg`,
    });

    const response = await api.post("/documents", formData);
    return response.data.data;
  }
}

export default new DriverService();
